import { StatusBadgeTone } from 'src/app/ui/status-badge/status-badge.component';

/**
 * Product lifecycle states. `Published` products are sellable and fed to the AI
 * engine; `Draft` products are neither.
 *
 * The API is not consistent about casing (the inventory filter sends
 * `Published` but `draft`), so every lookup here goes through a lowercase
 * normalisation rather than matching the raw value.
 */
const PRODUCT_STATUS_TONES: Record<string, StatusBadgeTone> = {
  published: 'success',
  draft: 'draft',
};

/** i18n keys under `inventory.ProductsHistoryStatus` are capitalised. */
const PRODUCT_STATUS_I18N_KEYS: Record<string, string> = {
  published: 'Published',
  draft: 'Draft',
};

const I18N_PREFIX = 'inventory.ProductsHistoryStatus';

function normalize(status?: string | null): string {
  return (status ?? '').trim().toLowerCase();
}

/** True when the product carries a status we can render a badge for. */
export function hasProductStatus(status?: string | null): boolean {
  return normalize(status) in PRODUCT_STATUS_TONES;
}

/** Badge tone for a product status; unknown values fall back to `undefined`. */
export function productStatusTone(status?: string | null): StatusBadgeTone {
  return PRODUCT_STATUS_TONES[normalize(status)] ?? 'undefined';
}

/**
 * Translation key for a product status, casing-normalised. Unknown values
 * resolve to the raw status so the UI degrades to the API value rather than
 * printing a missing-key path.
 */
export function productStatusI18nKey(status?: string | null): string {
  const key = PRODUCT_STATUS_I18N_KEYS[normalize(status)];
  return key ? `${I18N_PREFIX}.${key}` : (status ?? '');
}
